/**
 * ATL-1313 — the dates a payment transaction carries, decoded once, for every writer that wrote one.
 *
 * WHY THIS EXISTS
 *
 * `PaymentTransactionsScreen` read these fields with `x?.toDate?.() || x` and handed the result
 * straight to `date-fns`' `format()`. That expression is correct against a Firestore Timestamp and
 * a no-op against everything else: a string stays a string, `{_seconds,_nanoseconds}` stays an
 * object, and `format()` answers an unparseable value by THROWING `RangeError: Invalid time value`.
 * The throw happens inside JSX — `OperationsList`'s own try/catch wraps the section date, not the
 * caller's `renderItemFieldsToShow` — so React turns it into a fatal, the app relaunches, the list
 * reopens, and the app crash-loops until the merchant picks a different period.
 *
 * `transactions.payment_range` is an untyped jsonb snapshot with three writers, and each chose a
 * different encoding. Measured on production 2026-09-13, every non-deleted row that has one:
 *
 *   {"_seconds":…,"_nanoseconds":…}    17,600 rows / 89 orgs   the migration, via the admin SDK
 *   "2026-09-09 23:29:55.534+00"          584 rows / 36 orgs   create_supply_atomic / update_supply_payment_atomic
 *   "2026-09-13T19:00:00.000Z"             25 rows /  6 orgs   the frontend dual-write
 *
 * Of the three, only the last one renders. So on an organization reading from Supabase this screen
 * is fatal for almost every supply or salary payment it can show — not for an exotic one. The
 * ticket arrived naming the ISO rows as the suspects; they are in fact the only safe shape.
 *
 * `toNestedDate` (ATL-916, extended for the Postgres encoding by ATL-1313) is the decoder. It
 * returns `null` for a value that carries no readable date rather than substituting the wall clock,
 * and the screen renders that as a dash: an unknown date must look unknown (the ATL-868 mistake was
 * to invent one).
 */
import { toNestedDate } from '../../utils/nestedDateValue'

/** What the two read paths put on a transaction row; every date field is deliberately `unknown`. */
export interface PaymentTransactionLike {
  date?: unknown
  paymentRange?: { startDate?: unknown; endDate?: unknown } | null
}

export interface DecodedPaymentRange {
  startDate: Date | null
  endDate: Date | null
}

/** The instant the payment was made, in whichever encoding the row arrived. */
export function paymentTransactionDate(item: PaymentTransactionLike | null | undefined): Date | null {
  return toNestedDate(item?.date)
}

/**
 * The period a payment covers, or `null` when the row carries no period at all.
 *
 * A range with an unreadable bound is returned with that bound `null` rather than dropped whole:
 * half a period is still information, and the caller decides how to show it.
 */
export function paymentTransactionRange(
  item: PaymentTransactionLike | null | undefined,
): DecodedPaymentRange | null {
  const range = item?.paymentRange
  if (range == null || typeof range !== 'object') return null
  const startDate = toNestedDate(range.startDate)
  const endDate = toNestedDate(range.endDate)
  if (startDate === null && endDate === null) return null
  return { startDate, endDate }
}

/**
 * Does this row belong in a list filtered BY PAYMENT PERIOD rather than by transaction date?
 *
 * The screen used to inline this as `(startDate <= range.endDate && endDate >= range.startDate)`
 * over values it had not decoded. Comparing a string — or `{_seconds}` — with a `Date` through
 * `<=` does not throw; it coerces and answers `false`, so every migrated row silently vanished
 * from the payment-period view. A list that quietly omits payments is worse than one that
 * crashes, because nobody reports it.
 *
 * A row with no readable period falls back to its transaction date, which is what the screen
 * already did for a row with no period at all.
 */
export function paymentRangeOverlaps(
  item: PaymentTransactionLike,
  range: { startDate: Date; endDate: Date },
): boolean {
  const decoded = paymentTransactionRange(item)
  const startDate = decoded?.startDate
  const endDate = decoded?.endDate

  if (!startDate || !endDate) {
    const transactionDate = paymentTransactionDate(item)
    // Undecodable on both fields: keep the row. Dropping it would hide money on the strength of a
    // formatting problem, and the row still renders — with a dash where its dates would be.
    if (!transactionDate) return true
    return transactionDate >= range.startDate && transactionDate <= range.endDate
  }

  // Ranges overlap when (startA <= endB) && (endA >= startB).
  return startDate <= range.endDate && endDate >= range.startDate
}
